import { useCallback, useEffect, useRef, useState } from 'react';

import { differenceInMilliseconds } from 'date-fns';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash';

import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';

import {
  detailDashParamsAtom,
  detailDashboardUrlAtom,
  detailDrawerOpenAtom,
  filtersAtom,
  isDevDashboardAtom,
  paramsAtom,
  tableauSheetAtom,
} from './atoms';
import { DashboardUrls, parameterNames } from './constants';
import { TableauEventType } from './types';
import type {
  Dashboard,
  FilterChangedEvent,
  ParameterChangedEvent,
  ParameterName,
  TabSwitchedEvent,
  TableauFilterState,
  TableauMarkSelectionEvent,
  Viz,
  VizLoadErrorEvent,
} from './types';

// The useTableauViz hook is responsible for adding and removing event listeners
// to/from the tableau-viz element. The event listeners are constructed in other
// hooks.
export const useTableauViz = (
  token: string | undefined,
  refetchToken: () => void,
  dashboard: Dashboard,
) => {
  const tableauVizLoadStart = useRef<Date | undefined>();
  const handleDetailDashboard = useTableauDetailDashboard(dashboard);
  const handleParameterChanged = useTableauParameterChanged(dashboard);
  const handleFilterChanged = useTableauFilterChanged(dashboard);
  const {
    handleFirstInteractive,
    handleIframeSrcUpdated,
    handleResize,
    handleVizLoadError,
    isLoadingViz,
    isVizLoadError,
  } = useTableauVizLoad(dashboard, refetchToken, tableauVizLoadStart);
  const handleTabSwitched = useTableauTabSwitched(dashboard);
  useEffect(() => {
    if (token) {
      tableauVizLoadStart.current = new Date();
      window.addEventListener('resize', handleResize);

      const handlers = {
        [TableauEventType.FirstInteractive]: handleFirstInteractive,
        [TableauEventType.VizLoadError]: handleVizLoadError,
        [TableauEventType.IframeSrcUpdated]: handleIframeSrcUpdated,
        [TableauEventType.ParameterChanged]: handleParameterChanged,
        [TableauEventType.FilterChanged]: handleFilterChanged,
        [TableauEventType.MarkSelectionChanged]: handleDetailDashboard,
        [TableauEventType.TabSwitched]: handleTabSwitched,
      };

      const vizElement = document.getElementById(`tableau-viz-${dashboard}`);
      Object.entries(handlers).forEach(([event, handler]) => {
        vizElement?.addEventListener(event, handler);
      });

      return () => {
        window.removeEventListener('resize', handleResize);
        Object.entries(handlers).forEach(([event, handler]) => {
          vizElement?.removeEventListener(event, handler);
        });
      };
    }
  }, [
    dashboard,
    handleDetailDashboard,
    handleFilterChanged,
    handleFirstInteractive,
    handleIframeSrcUpdated,
    handleParameterChanged,
    handleResize,
    handleTabSwitched,
    handleVizLoadError,
    token,
  ]);

  return { isLoadingViz, isVizLoadError };
};

const useTableauVizLoad = (
  dashboard: Dashboard,
  refetchToken: () => void,
  tableauVizLoadStart: React.MutableRefObject<Date | undefined>,
) => {
  const [isLoadingViz, setIsLoadingViz] = useState(true);
  const [isVizLoadError, setIsVizLoadError] = useState(false);
  const [refetchCount, setRefetchCount] = useState(0);
  const isDevDashboard = useAtomValue(isDevDashboardAtom);
  const tableauSheet = useAtomValue(tableauSheetAtom);
  const { trackEvent } = useTrackEvent();

  const handleTrackTableauVizLoad = useCallback(
    (newError?: string) => {
      trackEvent(mixpanelEvents.tableauVizLoad, {
        dashboard,
        error: newError,
        isDevDashboard,
        loadTime: tableauVizLoadStart.current
          ? differenceInMilliseconds(new Date(), tableauVizLoadStart.current)
          : undefined,
      });
    },
    [dashboard, isDevDashboard, tableauVizLoadStart, trackEvent],
  );

  const handleFirstInteractive = useCallback(async () => {
    if (dashboard === 'CLINICAL_MENTORING_BETA' && tableauSheet) {
      const vizElement = document.getElementById(`tableau-viz-${dashboard}`);
      const workbook = await (vizElement as Viz)?.workbook;
      workbook?.activateSheetAsync(tableauSheet);
    }
    setIsLoadingViz(false);
    setIsVizLoadError(false);
    handleTrackTableauVizLoad();
  }, [dashboard, handleTrackTableauVizLoad, tableauSheet]);

  const handleVizLoadError = useCallback(
    async (event: Event) => {
      if (!('detail' in event)) return;

      const loadErrorEvent = event as VizLoadErrorEvent;
      const message = loadErrorEvent.detail?.message
        ? JSON.parse(loadErrorEvent.detail.message)
        : undefined;
      const errorMessage = message?.errorMessage
        ? JSON.parse(message.errorMessage)
        : undefined;
      const statusCode = message?.statusCode;
      const errorCode = loadErrorEvent.detail?.errorCode;
      const code = errorMessage?.result?.errors?.[0]?.code;
      let newError;
      if (statusCode && errorCode && code) {
        newError = `${statusCode}: ${errorCode} with error code ${code}`;
      } else {
        newError = 'Error unknown';
      }
      handleTrackTableauVizLoad(newError);

      if (errorCode === 'auth-failed' && refetchCount < 2) {
        setIsLoadingViz(true);
        refetchToken();
        setRefetchCount(refetchCount + 1);
      } else {
        setIsLoadingViz(false);
        setIsVizLoadError(true);
      }
    },
    [handleTrackTableauVizLoad, refetchCount, refetchToken],
  );

  // Invoked when user switches dashboards and `src` attribute changes
  const handleIframeSrcUpdated = () => {
    setIsLoadingViz(true);
    tableauVizLoadStart.current = new Date();
  };

  // Resizes the tableau-viz element to fit its container
  const handleResize = useCallback(() => {
    const vizElementContainer = document.getElementById(
      'tableau-viz-container',
    );
    const height = vizElementContainer?.clientHeight;
    const width = vizElementContainer?.clientWidth;

    const vizElement = document.getElementById(`tableau-viz-${dashboard}`);
    if (vizElement) {
      height && vizElement.setAttribute('height', `${height}px`);
      width && vizElement.setAttribute('width', `${width}px`);
    }
  }, [dashboard]);

  return {
    handleFirstInteractive,
    handleIframeSrcUpdated,
    handleResize,
    handleTrackTableauVizLoad,
    handleVizLoadError,
    isLoadingViz,
    isVizLoadError,
  };
};

const useTableauDetailDashboard = (mainDashboard: Dashboard) => {
  const setDetailDashboardUrl = useSetAtom(detailDashboardUrlAtom);
  const setDetailDrawerOpen = useSetAtom(detailDrawerOpenAtom);
  const setDetailDashParams = useSetAtom(detailDashParamsAtom);

  const isDevDashboard = useAtomValue(isDevDashboardAtom);

  const { trackEvent } = useTrackEvent();

  const getParameterValues = useCallback(async () => {
    const vizElement = document.getElementById(`tableau-viz-${mainDashboard}`);
    const parameters = await (vizElement as Viz)?.workbook.getParametersAsync();

    const parameterValues = parameterNames.reduce(
      (acc, paramName: ParameterName) => {
        const value = parameters.find(
          ({ parameterImpl }) => parameterImpl.name === paramName,
        )?.parameterImpl.currentValue.value;
        if (value) {
          acc[paramName] = value;
        }
        return acc;
      },
      {} as Record<ParameterName, string>,
    );

    return parameterValues;
  }, [mainDashboard]);

  return useCallback(
    async (event: Event) => {
      if (!('detail' in event)) return;

      const markSelectionEvent = event as TableauMarkSelectionEvent;
      const marks = await markSelectionEvent.detail.getMarksAsync();
      const mark = marks.data.find((item: any) => item.data.length);
      const providerNameIndex = mark?.columns.findIndex(
        (column: any) => column.fieldName.trim() === 'Provider Name',
      );
      const practiceNameIndex = mark?.columns.findIndex(
        (column: any) => column.fieldName.trim() === 'Practice Name',
      );
      const parameterValues = await getParameterValues();

      const providerName =
        typeof providerNameIndex === 'number'
          ? mark?.data[0][providerNameIndex]?.value
          : undefined;
      const practiceName =
        typeof practiceNameIndex === 'number'
          ? mark?.data[0][practiceNameIndex]?.value
          : undefined;

      if (!practiceName && !providerName) return;

      // This is a temporary solution to avoid opening the detail drawer from the practice level dashboard
      if (!providerName && mainDashboard !== 'HYGIENE_TASKS') {
        return;
      }

      // Using detailPracticeName and detailProviderName to avoid conflict with default property names
      trackEvent(mixpanelEvents.tableauTableRowClick, {
        dashboard: mainDashboard,
        detailPracticeName: practiceName,
        detailProviderName: providerName,
        isDevDashboard,
      });

      let detailDashUrl = '';
      if (mainDashboard === 'CLINICAL_MENTORING_BETA') {
        detailDashUrl =
          DashboardUrls.PROVIDER_DETAIL[isDevDashboard ? 'dev' : 'prod']!;
      } else if (mainDashboard === 'HYGIENE_TASKS') {
        detailDashUrl =
          DashboardUrls.PRACTICE_DETAIL[isDevDashboard ? 'dev' : 'prod']!;
      }

      setDetailDashParams({
        ...parameterValues,
        'Provider Name': providerName,
        'Practice Name': practiceName,
      });
      setDetailDashboardUrl(detailDashUrl);
      setDetailDrawerOpen(true);
    },
    [
      getParameterValues,
      isDevDashboard,
      mainDashboard,
      setDetailDashParams,
      setDetailDashboardUrl,
      setDetailDrawerOpen,
      trackEvent,
    ],
  );
};

const useTableauParameterChanged = (dashboard: Dashboard) => {
  const isDevDashboard = useAtomValue(isDevDashboardAtom);
  const [parameters, setParameters] = useAtom(paramsAtom);
  const { trackEvent } = useTrackEvent();

  // When a parameter is changed, the ParameterChanged event fires on every
  // Tableau worksheet that the dashboard is comprised of. Let's debounce
  // setParameters to avoid too many setState calls that cause re-renders and to
  // avoid sending too many Mixpanel events.
  const debouncedSetParameters = debounce(
    (
      parameterName: string,
      parameterValue: string | number | boolean | undefined,
    ) => {
      setParameters({
        ...(parameters || {}),
        [parameterName]: parameterValue,
      });

      trackEvent(mixpanelEvents.tableauParameterChanged, {
        dashboard,
        isDevDashboard,
        parameterName,
        parameterValue,
      });
    },
    1000,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    return () => {
      debouncedSetParameters.cancel();
    };
  }, [debouncedSetParameters]);

  return useCallback(
    async (event: Event) => {
      if (!('detail' in event)) return;

      const parameterEvent = event as ParameterChangedEvent;
      const parameter = await parameterEvent.detail.getParameterAsync();
      const parameterName = parameter.parameterImpl.name;
      const parameterValue = parameter.parameterImpl.currentValue.value;

      // Persist these parameters across main dashboard switches
      if (parameterNames.includes(parameterName as ParameterName)) {
        debouncedSetParameters(parameterName, parameterValue);
      }
    },
    [debouncedSetParameters],
  );
};

const useTableauFilterChanged = (dashboard: Dashboard) => {
  const isDevDashboard = useAtomValue(isDevDashboardAtom);
  const { trackEvent } = useTrackEvent();
  const [filters, setFilters] = useAtom(filtersAtom);

  // When a filter is changed, the FilterChanged event fires on every Tableau
  // worksheet that the dashboard is comprised of. Let's debounce setFilters to
  // avoid too many setState calls that cause re-renders and to avoid sending
  // too many Mixpanel events.
  const debouncedSetFilters = debounce(
    (filterName: string, filterValue: TableauFilterState) => {
      if (filterName.includes('Action (')) {
        return;
      }

      setFilters({
        ...filters,
        [filterName]: filterValue,
      });

      // Rather than sending all selected values to Mixpanel (e.g., 100 practice
      // names), let's send a single summary string. Note Tableau quirk:
      // appliedValues is an empty array when all values are selected.
      let trackedFilterValue:
        | 'ALL'
        | 'MULTIPLE'
        | 'SINGLE'
        | 'NONE'
        | undefined;
      if (filterValue.isAllSelected) {
        trackedFilterValue = 'ALL';
      } else if (
        filterValue.appliedValues &&
        filterValue.appliedValues.length > 1
      ) {
        trackedFilterValue = 'MULTIPLE';
      } else if (filterValue.appliedValues?.length === 1) {
        trackedFilterValue = 'SINGLE';
      } else {
        trackedFilterValue = 'NONE';
      }

      trackEvent(mixpanelEvents.tableauFilterChanged, {
        dashboard,
        filterName,
        filterValue: trackedFilterValue,
        isDevDashboard,
      });
    },
    1000,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetFilters]);

  return useCallback(
    async (event: Event) => {
      if (!('detail' in event)) return;

      const filterEvent = event as FilterChangedEvent;
      const filter = await filterEvent.detail.getFilterAsync();
      const appliedValues = filter.appliedValues?.map(({ value }) => value);

      if (appliedValues) {
        debouncedSetFilters(filter.fieldName, {
          appliedValues,
          isAllSelected: filter.isAllSelected,
        });
      }
    },
    [debouncedSetFilters],
  );
};

const useTableauTabSwitched = (dashboard: Dashboard) => {
  const setTableauSheet = useSetAtom(tableauSheetAtom);
  // When a parameter is changed, the ParameterChanged event fires on every
  // Tableau worksheet that the dashboard is comprised of. Let's debounce
  // setParameters to avoid too many setState calls that cause re-renders and to
  // avoid sending too many Mixpanel events.
  const debouncedSetTab = debounce(
    (parameterValue: string) => {
      if (dashboard === 'CLINICAL_MENTORING_BETA') {
        setTableauSheet(parameterValue);
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    return () => {
      debouncedSetTab.cancel();
    };
  }, [debouncedSetTab]);

  return useCallback(
    async (event: Event) => {
      if (!('detail' in event)) return;
      const tabSwitchedEvent = event as TabSwitchedEvent;
      const tab = await tabSwitchedEvent.detail.newSheetName;
      debouncedSetTab(tab);
    },
    [debouncedSetTab],
  );
};
